import { useContext, useEffect } from 'react';
import { MapContext } from '../../context/MapContext';
import './SelectOption.css';
import axios from 'axios';
import {
	householdLayers,
	getAgglomerateFeatures,
	getLocationFeatures,
	getIdsFeatures,
	poiLayers,
	getZoomExtent,
} from '../content/layer';
import { cql2filter } from '../../common/func/cql2filter';
import { distanceList, poiList } from '../../common/data/list';
import { useState } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { AppContext } from '../../context/AppContext';
import GridLegend from './GridLegend';
import { Fill, Stroke, Style } from 'ol/style';

const style = {
	selectedGrid: new Style({
		fill: new Fill({ color: '#228FA1' }),
		stroke: new Stroke({ color: 'rgba(0, 0, 0, 0)', width: 3 }),
		zIndex: 1,
	}),
};

let selected = null;
export default function SelectOption() {
	const {
		mode,
		map,
		setAggGrid,
		setCollapsePopGrid,
		sidoList,
		setSidoList,
		sidoCd,
		setSidoCd,
		sggList,
		setSggList,
		sggCd,
		setSggCd,
		minInput,
		setMinInput,
		maxInput,
		setMaxInput,
		selectPoi,
		setSelectPoi,
		selectDistance,
		setSelectDistance,
		selectStandard,
		setSelectStandard,
		agglomerate,
		location,
		industry,
		setPopupOpen,
		setPopupData,
		setPopupPosition,
		getPoi,
	} = useContext(MapContext);
	const { setBackdropOpen } = useContext(AppContext);

	const [snackBarOpen, setSnackBarOpen] = useState(false);
	const [dropDownOpen, setDropDownOpen] = useState(false);
	const [resultOpen, setResultOpen] = useState(false);

	const getSidoOptions = sidoList.map((sido) => {
		return (
			<option key={sido.code} value={sido.code}>
				{sido.name}
			</option>
		);
	});

	const getSggOptions = sggList.map((sgg) => {
		return (
			<option key={sgg.code} value={sgg.code}>
				{sgg.name}
			</option>
		);
	});

	const getPoiOptions = poiList.map((poi, idx) => {
		return (
			<option key={idx} value={poi.layer}>
				{poi.name}
			</option>
		);
	});

	const getDistanceOptions = distanceList.map((distance) => {
		return (
			<option key={distance.target} value={distance.target}>
				{distance.name}
			</option>
		);
	});

	const handleChangeSidoCd = (e) => {
		if (e.target.value === 'disabled') {
			setSidoCd('');
		} else {
			setSidoCd(e.target.value);
		}
		setSggCd('');
	};

	const handleChangeSggCd = (e) => {
		if (e.target.value === 'disabled') {
			setSggCd('');
		} else {
			setSggCd(e.target.value);
		}
	};

	const handleMinInput = (e) => {
		setMinInput(Number(e.target.value));
	};

	const handleMaxInput = (e) => {
		setMaxInput(Number(e.target.value));
	};

	const initializeLocation = () => {
		setSelectPoi('');
		setSelectDistance('');
		setSelectStandard('');
	};

	const handleChangeLocation = (e) => {
		if (e.target.id === 'poiName') {
			setSelectPoi(e.target.value);
			setSelectDistance('');
			setSelectStandard('');
		} else if (e.target.id === 'distance') {
			setSelectDistance(e.target.value);
			setSelectStandard('');
		} else if (e.target.id === 'standard') {
			setSelectStandard(e.target.value);
		}

		if (e.target.value === 'disabled') {
			initializeLocation();
		}
	};

	const gridClickCallBack = (selectedFeature, layer) => {
		if (layer.get('title') === '집적지 분석 격자' || layer.get('title') === '입지분석 격자') {
			setPopupOpen(true);
			setPopupData({
				sido_nm: selectedFeature.values_.sido_nm,
				sgg_nm: selectedFeature.values_.sgg_nm,
				id: `${selectedFeature.values_.grid_1k_cd}`,
				toH: selectedFeature.values_.to_hh_21,
				oneH: selectedFeature.values_.one_hh_21,
				allPop: selectedFeature.values_.all_pop21,
				pop_49: selectedFeature.values_.pop_1549_2,
				pop_65: selectedFeature.values_.old_pop21,
				pop_75: selectedFeature.values_.f75_pop_16,
			});
			selected = selectedFeature;
			style.selectedGrid.getStroke().setColor(selectedFeature.get('COLOR') || '#00FFFF');
			selectedFeature.setStyle(style.selectedGrid);
			return true;
		} else {
		}
	};

	const onClickGridFeature = (e) => {
		if (selected !== null) {
			selected.setStyle(undefined);
			selected = null;
		}
		setPopupPosition({
			x: e.originalEvent.clientX,
			y: e.originalEvent.clientY,
		});
		map.forEachFeatureAtPixel(e.pixel, gridClickCallBack);
	};

	const getAgglomerateResearch = async (e, type) => {
		if (sidoCd !== '' || sggCd !== '') {
			const aggCqlFilter = await cql2filter(
				`sgg_cd like '${type}%' and one_hh_21 >= ${minInput} and one_hh_21 <= ${maxInput} and all_pop21 > 5`
			);
			await getAgglomerateFeatures(aggCqlFilter);
			agglomerate && householdLayers.sphAgglomerateLayer.setVisible(true);

			const zoomCqlFilter = await cql2filter(`sgg_cd like '${type}%'`);
			await getZoomExtent(zoomCqlFilter, map);

			map.on('click', (e) => onClickGridFeature(e));

			getPoi(type);

			const cqlFilter = await cql2filter(`sgg_cd like '${type}%'`);
			[poiLayers.industryPolygon, householdLayers.sphAggGridLayer, householdLayers.sphPopGridLayer].forEach((layer) => {
				layer.getSource().updateParams({
					filter: cqlFilter,
				});
				layer.getSource().refresh();
			});

			householdLayers.sphLocationLayer.getSource().clear();
			householdLayers.sphLocationLayer.setVisible(false);
			householdLayers.sphIdsLayer.getSource().clear();
			householdLayers.sphIdsLayer.setVisible(false);
			initializeLocation();
		} else if (sidoCd === '') {
			setSnackBarOpen(true);
		}
		setBackdropOpen(false);
	};

	const getLocationResearch = async (e) => {
		if (selectPoi !== '' && selectDistance !== '' && selectStandard !== '') {
			const viewParams =
				sggCd !== ''
					? `code_type:sgg_cd;code:${sggCd};layer_name:${selectPoi};field_name:${selectDistance};value:${selectStandard};min:${minInput};max:${maxInput};`
					: sggCd === ''
					? `code_type:sido_cd;code:${sidoCd};layer_name:${selectPoi};field_name:${selectDistance};value:${selectStandard};min:${minInput};max:${maxInput};`
					: null;
			await getLocationFeatures(viewParams);
			location && householdLayers.sphLocationLayer.setVisible(true);

			if (selectPoi === 'sph_grid_ids') {
				const idsViewParams =
					sggCd !== ''
						? `code_type:sgg_cd;code:${sggCd};field_name:${selectDistance};value:${selectStandard};min:${minInput};max:${maxInput};`
						: sggCd === ''
						? `code_type:sido_cd;code:${sidoCd};field_name:${selectDistance};value:${selectStandard};min:${minInput};max:${maxInput};`
						: null;

				await getIdsFeatures(idsViewParams);
				industry && householdLayers.sphIdsLayer.setVisible(true);
			} else {
				householdLayers.sphIdsLayer.setVisible(false);
			}

			map.on('click', (e) => onClickGridFeature(e));
		} else {
			setSnackBarOpen(true);
		}
		setBackdropOpen(false);
	};

	const onClickResearchBtn = (e) => {
		setBackdropOpen(true);
		const type = sggCd !== '' ? sggCd : sggCd === '' ? sidoCd : null;
		if (mode === 'agglomerate') {
			getAgglomerateResearch(e, type);
		} else if (mode === 'location') {
			getLocationResearch(e);
		}

		setResultOpen(true);
		setCollapsePopGrid(false);
		householdLayers.sphPopGridLayer.setVisible(false);
		setAggGrid(false);
		householdLayers.sphAggGridLayer.setVisible(false);

		setPopupOpen(false);
	};

	useEffect(() => {
		axios({
			method: 'GET',
			url: `${process.env.REACT_APP_CONTEXT_PATH}/interactive/api/admCtpvnListByPeriod.json?period=202112`,
		})
			.then((res) => {
				setSidoList(res.data.data);
			})
			.catch((error) => {
				console.log(error);
			});
	}, []);

	useEffect(() => {
		if (sidoCd) {
			axios({
				method: 'GET',
				url: `${process.env.REACT_APP_CONTEXT_PATH}/interactive/api/admSggListByPeriod.json?ctpvn_code=${sidoCd}&period=202206`,
			})
				.then((res) => {
					const data = res.data.data;
					const sortedData = data.sort((a, b) => {
						if (a.name > b.name) return 1;
						if (a.name < b.name) return -1;
						return 0;
					});
					setSggList(sortedData);
				})
				.catch((error) => {
					console.log(error);
				});
		} else {
			setSggList([]);
		}
	}, [sidoCd]);

	return (
		<>
			{mode !== 'intro' ? (
				<div className="sideMenu">
					<div className="scrollArea">
						{mode === 'agglomerate' ? (
							<>
								<div className="listArea">
									<div className="tit">대상지역</div>
									<div className="listInner">
										<form>
											<label>시도 선택</label>
											<select id="sido" value={sidoCd} onChange={handleChangeSidoCd}>
												<option value="disabled">시도 선택</option>
												{getSidoOptions}
											</select>
										</form>
										<form>
											<label>시군구 선택</label>
											<select id="sgg" value={sggCd} onChange={handleChangeSggCd}>
												<option value="disabled">시군구 선택</option>
												{getSggOptions}
											</select>
										</form>
									</div>
								</div>
								<div className="listArea">
									<div className="tit">분석조건</div>
									<div className="listInner">
										<form>
											<label>격자 당 1인가구 수</label>
											<input
												className=""
												type="number"
												step={0.1}
												min={0.0}
												max={19330.0}
												value={minInput}
												onChange={handleMinInput}
											></input>
											<span> 이상 ~ </span>
											<input
												className=""
												type="number"
												step={0.1}
												min={0.0}
												max={19330.0}
												value={maxInput}
												onChange={handleMaxInput}
											></input>
											<span> 이하 </span>
										</form>
									</div>
								</div>
								<div className="listArea">
									<div className="tit">
										<span className="whiteLabel">참고</span>
										분석 조건 설정 방법
										<button className="dropdownIcon" onClick={() => setDropDownOpen(!dropDownOpen)}>
											<img
												src="img/arrowIcon.png"
												alt="탭 활성화"
												className={dropDownOpen ? 'closeArrow' : 'closeArrow hide'}
											/>
										</button>
									</div>
									<div className={dropDownOpen ? 'dropdownCont' : 'dropdownCont hide'}>
										<h2 className="subTit">
											<img src="img/iconQuest.png" alt="iconQuest" />
											1인가구 통계
										</h2>
										<h3 className="subText">&lsquo;1인가구 수&rsquo; 기준을 정할 때 참고 하실 수 있어요!</h3>

										<table className="smTable">
											<thead>
												<tr>
													<th colSpan="2">
														전국 1인가구 수 <p>(1km × 1km 격자)</p>
													</th>
												</tr>
											</thead>
											<tbody>
												<tr>
													<td width="60%">평균</td>
													<td>66.8</td>
												</tr>
												<tr>
													<td>중위수</td>
													<td>5.0</td>
												</tr>
												<tr>
													<td>표준편차</td>
													<td>391.8</td>
												</tr>
												<tr>
													<td>최소값</td>
													<td>0.0</td>
												</tr>
												<tr>
													<td>최대값</td>
													<td>19,330.0</td>
												</tr>
											</tbody>
										</table>

										<table className="smTable">
											<thead>
												<tr>
													<th colSpan="2">
														시·군·구 거주인구 규모별 <br />
														1인가구 수 평균
														<p>(1km × 1km 격자)</p>
													</th>
												</tr>
											</thead>
											<tbody>
												<tr>
													<td width="60%">50만 이상</td>
													<td>982.7</td>
												</tr>
												<tr>
													<td>25만 이상~50만 미만</td>
													<td>1,037.2</td>
												</tr>
												<tr>
													<td>10만 이상~25만 미만</td>
													<td>485.8</td>
												</tr>
												<tr>
													<td>5만 이상~10만 미만</td>
													<td>178.8</td>
												</tr>
												<tr>
													<td>5만 미만</td>
													<td>79.7</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>
							</>
						) : mode === 'location' ? (
							<div className="listArea">
								<div className="tit">입지속성</div>
								<div className="listInner">
									<h2 className="subTit">주요시설과의 거리</h2>
									<h3 className="subText">주요시설과 거리(반경)를 적용할 수 있습니다.</h3>
									<div className="selectboxLine100">
										<select id="poiName" value={selectPoi} onChange={handleChangeLocation}>
											<option value="disabled">시설명</option>
											{getPoiOptions}
										</select>
									</div>
									<div className="selectboxLine50">
										<select id="distance" value={selectDistance} onChange={handleChangeLocation}>
											<option value="disabled">거리</option>
											{getDistanceOptions}
										</select>
										<select id="standard" value={selectStandard} onChange={handleChangeLocation}>
											<option value="disabled">기준</option>
											<option value="0">이외</option>
											<option value="1">이내</option>
										</select>
									</div>
								</div>
							</div>
						) : null}
						<div className="sidemenuBottom">
							<button className="sidemenuSearchBtn" onClick={onClickResearchBtn}>
								<img src="img/searchIcon.png" alt="분석수행" />
								분석수행
							</button>
							{mode === 'agglomerate' && (
								<p>
									※ 'Step 1. 집적지 분석' 수행 후, 1인가구 집적지의 입지속성을 분석하고 싶으시면 'Step 2. 입지속성
									분석'을 클릭해 주세요.
								</p>
							)}
						</div>
					</div>
					{snackBarOpen ? (
						<Snackbar
							className="alert"
							open={snackBarOpen}
							autoHideDuration={3000}
							onClose={() => setSnackBarOpen(false)}
						>
							<Alert onClose={() => setSnackBarOpen(false)} severity="info" sx={{ width: '100%' }}>
								<p className="snackbarContent">
									{sidoCd === ''
										? '대상지역을 선택해주세요.'
										: selectPoi === ''
										? '시설명을 선택해주세요.'
										: selectDistance === ''
										? '거리를 선택해주세요.'
										: selectStandard === ''
										? '기준을 선택해주세요.'
										: null}
								</p>
							</Alert>
						</Snackbar>
					) : null}
					{resultOpen && <GridLegend />}
				</div>
			) : null}
		</>
	);
}
